/**
 * ET-30 — treatment-preference / pricing-plan selection contract.
 *
 * Distinct from `schema.ts` (the THS questionnaire contract): there is no
 * Beluga titration/routing payload today, and THS's `save-response` response
 * carries only `{ questionnaireResponseId, status }`. `patientPreference`
 * here is NOT a value Beluga hands back — it's whichever branded/compounded
 * product + plan/frequency the customer picks on this screen, captured as a
 * preference and forwarded to cart. Non-PHI by construction: no answer
 * values or clinical fields belong in any type below.
 */
import { z } from "zod";

/**
 * Catalog variation-attribute id carrying the plan / supply frequency, shared
 * by the ET-30 plan selector and the ET-202 telehealth cart row so a catalog
 * rename can't break one surface without the other. (Scope note: this pins the
 * ID only. Each surface still derives its own display label, deliberately —
 * the cart row omits the line when the frequency is unresolvable, while the
 * selector falls back to "Plan N".)
 *
 * The cart row lost its `Plan:` line entirely in ET-202 and got it back in
 * ET-428, so between those two tickets the sentence above described a consumer
 * that didn't exist. It is accurate again — via `resolvePlanLabel` below.
 *
 * Verified against the live `weight-loss` catalog (ET-202 pre-flight,
 * 2026-08-12): id `frequency`, BM label "Shipping Plan", values
 * `monthly` → "Monthly" and `quarterly` → "3 Month". This previously read
 * `freq` in both call sites — correct for the interim catalog checked on
 * 2026-07-29, but stale afterwards, which silently dropped the cart row's
 * `Plan:` line and degraded the selector's options to "Plan 1"/"Plan 2".
 */
export const FREQUENCY_ATTRIBUTE_ID = "frequency";

/**
 * Known cadence values → the labels the catalog authors for them. Shared by
 * BOTH branches of `resolvePlanLabel`, so a `quarterly` variation value whose
 * authored name is missing still resolves instead of being unreachable on one
 * path.
 *
 * Deliberately a closed set: an unrecognised value yields `null` rather than
 * being title-cased through, because a "Fortnightly" invented here would look
 * exactly as authoritative as a real authored label. Note `quarterly` is
 * authored "3 Month" — which is why this cannot be derived from the raw value.
 *
 * Scope: this is the CART ROW's fallback, not a shared catalog map. The ET-30
 * plan selector has its own, different fallback for the same missing-name case
 * (`derivePlanOptions` renders `String(value)`, i.e. "quarterly"), so the two
 * surfaces can disagree in that state. Left that way on purpose — the selector
 * is picking between options where the raw value is at least unambiguous,
 * while the cart row is making a standalone claim about one plan. Unifying
 * them means agreeing one fallback, not just exporting this constant.
 */
const CADENCE_LABELS: Record<string, string> = {
  monthly: "Monthly",
  quarterly: "3 Month",
};

/**
 * Human-readable plan / supply cadence for a telehealth product, or `null` when
 * the catalog doesn't prove one (ET-428).
 *
 * Two sources, in this order, because the telehealth catalog has two SHAPES —
 * confirmed against all 9 live SKUs in the ET-428 pre-flight (2026-09-25):
 *
 *  1. **The `frequency` variation attribute's authored value name.** Only the
 *     two compounded masters (and their variants) carry one. Preferred because
 *     the label is merchandiser-authored and NOT derivable from the raw value:
 *     `quarterly` is authored "3 Month", not "Quarterly". Deriving our own
 *     casing here would silently diverge from the plan selector, which reads
 *     the same authored names.
 *  2. **`c_frequency`.** Populated on every telehealth SKU including the seven
 *     single-SKU branded ones (Wegovy, Ozempic, Zepbound, …) that have no
 *     variation attribute at all. Without this fallback the cart row's `Plan:`
 *     line would appear on 2 of the 9 live SKUs.
 *
 * Returns `null` rather than guessing, matching `telehealthPricePeriod`'s
 * fail-closed convention (`product-details/components/telehealth/price-period.ts`):
 * a wrong cadence on a subscription card is worse than no cadence. That also
 * means an attribute-id rename degrades to an absent line, not a wrong one.
 */
export function resolvePlanLabel(product: {
  variationAttributes?: Array<{
    id?: string;
    values?: Array<{ value?: string; name?: string }> | null;
  }> | null;
  variationValues?: { [key: string]: unknown } | null;
  c_frequency?: string | null;
}): string | null {
  const selected = product.variationValues?.[FREQUENCY_ATTRIBUTE_ID];

  // A variation VALUE, once present, is authoritative — `c_frequency` must
  // never be consulted past this branch. On a compounded variant the MASTER's
  // `c_frequency` can read `monthly` while the selected variant is
  // `quarterly`, so falling through on a missed authored-name lookup (the
  // attribute trimmed out of a `select`, or the id renamed — `freq` →
  // `frequency` already happened once) would print "Plan: Monthly" on a
  // 3-month plan. A wrong cadence is the single outcome this function exists
  // to prevent, so this branch returns its own answer or `null`, never the
  // other source's.
  if (typeof selected === "string") {
    const authored = product.variationAttributes
      ?.find(attribute => attribute?.id === FREQUENCY_ATTRIBUTE_ID)
      ?.values?.find(value => value?.value === selected)?.name;
    return authored || (CADENCE_LABELS[selected] ?? null);
  }

  // Only reached by the single-SKU shape, which carries no variation values at
  // all. `c_frequency` is `monthly` on every such live product today.
  return CADENCE_LABELS[product.c_frequency ?? ""] ?? null;
}

/** Which product family the customer expressed a preference for. Not a
 *  guaranteed prescription — provider-reviewed downstream. */
export const treatmentPreferenceTypeSchema = z.enum(["branded", "compounded"]);
export type TreatmentPreferenceType = z.infer<
  typeof treatmentPreferenceTypeSchema
>;

/**
 * The clinically-routed pathway label surfaced as read-only display context,
 * when the product data carries one. Optional and lenient by design — Beluga
 * has no published routing taxonomy yet, so an unrecognized/absent value
 * simply means nothing is shown, not a parse failure.
 */
export const treatmentPathwaySchema = z
  .enum(["starting", "continuation", "reduction", "restart"])
  .optional();
export type TreatmentPathway = z.infer<typeof treatmentPathwaySchema>;

/** One customer-selectable plan/frequency option on a given product. */
export const planOptionSchema = z.object({
  variantProductId: z.string(),
  frequencyLabel: z.string(),
  price: z.number(),
  compareAtPrice: z.number().optional(),
});
export type PlanOption = z.infer<typeof planOptionSchema>;

/**
 * The customer's captured, non-clinical preference — forwarded to cart via
 * the ET-30 basket action. `questionnaireResponseId` is the ET-133 THS
 * submission id (a linking reference only, never the underlying answers).
 */
export const patientPreferenceSchema = z.object({
  treatmentPreferenceType: treatmentPreferenceTypeSchema,
  productId: z.string(),
  selectedPlan: planOptionSchema,
  treatmentPathway: treatmentPathwaySchema,
  questionnaireResponseId: z.string(),
  conditionCode: z.string(),
  /**
   * ET-202 "Edit Plan": the basket line item this submission REPLACES, rather
   * than adding a second line. Present only when the shopper reached the plan
   * screen from the cart (`/intake/{slug}/plan?itemId=…`). Optional so the
   * post-intake first add — which has no line to replace — is unchanged.
   *
   * The id alone is not authority: the action re-reads the basket and requires
   * that line to already carry a `c_th_questionnaire_response_id`, so a crafted
   * submission can't retarget a non-telehealth line or graft an intake
   * reference onto one that never had it.
   */
  editItemId: z.string().optional(),
});
export type PatientPreference = z.infer<typeof patientPreferenceSchema>;
